<template>
  <div class="tw-text-dt-title-4 sm:tw-text-dt-title-3">
    <!-- Product Options Section: Shows individual options with their prices -->
    <div
      v-if="showOptionList && options.length > 0"
      class="tw-flex tw-flex-col tw-gap-3"
    >
      <div
        v-for="(val, index) in options"
        :key="`price-display-item-${val.name}`"
        class="tw-flex"
        :class="[
          optionsContainerClass,
          {
            'tw-border-b tw-border-light-gray-2':
              showBorderOptionBottom && index === options.length - 1,
          },
        ]"
      >
        <!-- Option Name -->
        <div
          class="tw-text-black tw-pr-4"
          :class="[
            optionNameClass,
            showBorderOptionName &&
              options.length <= 1 &&
              'tw-border-r tw-border-light-gray-2',
            showPaddingOptionPriceNameForIS &&
              (index === 0
                ? 'tw-pr-5 tw-pb-3'
                : index === options.length - 1
                  ? 'tw-pt-3 tw-pr-5'
                  : 'tw-pt-3 tw-pr-5 tw-pb-3'),
          ]"
        >
          {{ val.name }}
        </div>

        <!-- Option Price Display: Shows discounted and original price if applicable -->
        <div :class="optionPriceContainerClass">
          <div
            v-if="hasDiscount(val)"
            class="tw-flex"
            :class="optionDiscountedContainerClass"
          >
            <div :class="discountedClass">
              {{ formatPrice(val.price) }}
            </div>
            <div class="tw-ml-2" :class="optionPriceClass">
              {{ formatPrice(val.priceOfDiscounted) }}
            </div>
          </div>
          <div
            v-else-if="val.price"
            class="tw-text-black"
            :class="regularOptionPriceClass"
          >
            {{ formatPrice(val.price) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Divider between options and main price display -->
    <HorizontalLine
      v-if="showOptionsDivider && showBorderOptionList"
      :class="customHorizontalLine"
    />

    <!-- Main Price Display Section -->
    <div v-if="showMainPrice" class="tw-flex" :class="mainPriceContainerClass">
      <!-- Case 1: Regular Price Range (when no discount or same prices) -->
      <div
        v-if="isShowRegularPrice"
        class="tw-flex"
        :class="regularPriceContainerClass"
      >
        <div
          v-if="isSinglePrice(minPriceOption, maxPriceOption)"
          class="tw-text-black tw-mb-2"
        >
          {{ formatPrice(minPriceOption) }}
        </div>
        <div v-else class="tw-text-black tw-mb-2">
          {{ formatPriceRange(minPriceOption, maxPriceOption) }}
        </div>
      </div>

      <!-- Case 2: Discounted Price Range (when options have discounts) -->
      <div
        v-else-if="isShowDiscountedPrice"
        class="tw-flex"
        :class="discountedPriceRangeContainerClass"
      >
        <!-- Discounted Price -->
        <div
          v-if="
            isSingleDiscountedPrice &&
            minPriceOfDiscountedOption !== maxPriceOption
          "
          class="tw-text-danger-toast"
          :class="mainPriceClass"
        >
          {{ formatPrice(minPriceOfDiscountedOption) }}
        </div>
        <div
          v-else-if="!isSingleDiscountedPrice"
          class="tw-text-danger-toast"
          :class="mainPriceClass"
        >
          {{
            formatPriceRange(
              minPriceOfDiscountedOption,
              maxPriceOfDiscountedOption,
            )
          }}
        </div>

        <!-- Original Price (shown as strikethrough) -->
        <div
          v-if="isSinglePrice(minPriceOption, maxPriceOption)"
          :class="discountedClass"
        >
          {{ formatPrice(minPriceOption) }}
        </div>
        <div v-else :class="discountedClass">
          {{ formatPriceRange(minPriceOption, maxPriceOption) }}
        </div>
      </div>

      <!-- Case 3: Single Product with Discount -->
      <div
        v-else-if="showSingleProductDiscount"
        class="tw-flex"
        :class="singleProductDiscountContainerClass"
      >
        <div class="tw-text-danger-toast" :class="mainPriceClass">
          {{ formatPrice(priceOfDiscounted) }}
        </div>
        <div :class="discountedClass">
          {{ formatPrice(price) }}
        </div>
      </div>

      <!-- Case 4: Single Product Regular Price -->
      <div
        v-else-if="showSingleProductPrice"
        class="tw-text-black"
        :class="singleProductPriceContainerClass"
      >
        {{ formatPrice(price) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { $currencyFormat } = useNuxtApp();
const currencyStore = useCurrencyStore();
const currencySymbol = computed(() => currencyStore.currentSymbol);

// Make sure currency symbol is always available for SSR
const displayCurrencySymbol = computed(() => {
  // Use the store value if available, or default to the Thai Baht symbol
  // which is the same value used in the store initialization
  return currencySymbol.value || "฿";
});

const props = withDefaults(
  defineProps<{
    // Display configuration props
    showOptionList?: boolean;
    showBorderOptionList?: boolean; // ควบคุม border option ที่เป็น default อยู่แล้ว
    showBorderOptionBottom?: boolean; // โชว์ border bottom สำหรับ product
    showMarginBetweenOptions?: boolean; // ควบคุม margin ว่าจะให้โชว์หรือไม่โชว์
    showPaddingOptionPriceNameForIS?: boolean; // ควบคุม padding name of option price
    showBorderOptionName?: boolean; // ควบคุม border-r ของชื่อ option

    options: ProductOption[];
    minPriceOption?: number;
    maxPriceOption?: number;
    minPriceOfDiscountedOption?: number;
    maxPriceOfDiscountedOption?: number;
    price?: number;
    priceOfDiscounted?: number;

    // Class customization props for each section
    optionsContainerClass?: string | object | string[] | object[];
    mainPriceContainerClass?: string | object | string[] | object[];
    regularPriceContainerClass?: string | object | string[] | object[];
    discountedPriceRangeContainerClass?: string | object | string[] | object[];
    singleProductDiscountContainerClass?: string | object | string[] | object[];
    singleProductPriceContainerClass?: string | object | string[] | object[];
    discountedClass?: string | object | string[] | object[];
    mainPriceClass?: string | object | string[] | object[]; // ราคาหลัก ที่ไม่ใช่ discounted
    optionPriceClass?: string | object | string[] | object[]; // ราคา option ที่ไม่ใช่ discounted
    regularOptionPriceClass?: string | object | string[] | object[]; // ราคา option ที่เป็นราคาเดี่ยวไม่มี discounted
    optionNameClass?: string | object | string[] | object[]; // ชื่อ option
    optionPriceContainerClass?: string | object | string[] | object[]; // wrapper ของ option price
    optionDiscountedContainerClass?: string | object | string[] | object[]; // layout ของ discounted + original price
    customHorizontalLine?: string;
  }>(),
  {
    showOptionList: true,
    showBorderOptionList: true,
    showBorderOptionBottom: false,
    showMarginBetweenOptions: true,
    showPaddingOptionPriceNameForIS: false,
    showBorderOptionName: true,

    options: () => [],
    minPriceOption: 0,
    maxPriceOption: 0,
    minPriceOfDiscountedOption: 0,
    maxPriceOfDiscountedOption: 0,
    price: 0,
    priceOfDiscounted: 0,

    optionsContainerClass: "tw-justify-between",
    mainPriceContainerClass: "tw-items-end",
    regularPriceContainerClass: "tw-gap-x-2 tw-items-end",
    discountedPriceRangeContainerClass: "tw-flex-col tw-gap-x-2",
    singleProductDiscountContainerClass: "tw-flex-col tw-items-start",
    singleProductPriceContainerClass: "",
    discountedClass: "tw-text-dt-body tw-line-through tw-text-gray",
    mainPriceClass: "",
    optionPriceClass: "tw-text-danger-toast",
    regularOptionPriceClass: "",
    optionNameClass: "tw-text-mb-title-4 sm:tw-text-dt-title-4",
    optionPriceContainerClass: "",
    optionDiscountedContainerClass: "tw-items-center",
    customHorizontalLine: "",
  },
);

/**
 * Determines if we should show the divider between options and main price
 * Only shows when options list appears and has at least 2 options
 */
const showOptionsDivider = computed(
  () => props.showOptionList && props.options.length > 1,
);

/**
 * Determines if product has options
 * Used to control display logic based on whether we have options or not
 */
const hasOptions = computed(() => props.options.length > 0);

/**
 * Determines if main price section should be displayed
 * Shows when we have product options or a single product with price/discount
 */
const showMainPrice = computed(() => {
  return (
    hasOptions.value ||
    (!hasOptions.value && (props.price || props.priceOfDiscounted))
  );
});

/**
 * Case 1: Shows regular price range when there's no discount
 * Applies when all options are at regular price (no discounts)
 */
const isShowRegularPrice = computed(() => {
  return (
    hasOptions.value &&
    props.options.length > 1 &&
    props.minPriceOfDiscountedOption !== null &&
    props.minPriceOfDiscountedOption !== 0 &&
    props.minPriceOption !== null &&
    props.minPriceOption !== 0 &&
    props.minPriceOption === props.minPriceOfDiscountedOption &&
    props.maxPriceOfDiscountedOption === props.maxPriceOption
  );
});

/**
 * Case 2: Shows discounted price range for multiple options
 * Applies when we have product options with differing prices
 */
const isShowDiscountedPrice = computed(() => {
  return (
    hasOptions.value &&
    props.options.length > 1 &&
    props.minPriceOfDiscountedOption !== null &&
    props.minPriceOfDiscountedOption !== 0 &&
    props.minPriceOption !== null &&
    props.minPriceOption !== 0
  );
});

/**
 * Case 3: Shows single product discount when not displaying options
 * Applies when a single product has both regular and discounted prices
 */
const showSingleProductDiscount = computed(() => {
  return (
    !hasOptions.value &&
    props.priceOfDiscounted < props.price &&
    props.priceOfDiscounted !== 0
  );
});

/**
 * Case 4: Shows regular price for a single product
 * Applies when we have a single product with only a regular price
 */
const showSingleProductPrice = computed(() => {
  return !hasOptions.value && props.price;
});

/**
 * Helper to check if discounted and regular prices are the same
 */
const isSingleDiscountedPrice = computed(() => {
  return props.minPriceOfDiscountedOption === props.maxPriceOfDiscountedOption;
});

/**
 * Helper to check if a product option has a discount
 */
function hasDiscount(option: ProductOption): boolean {
  return (
    option.priceOfDiscounted < option.price && option.priceOfDiscounted !== 0
  );
}

/**
 * Helper to check if min and max prices are the same (single price)
 */
function isSinglePrice(min: number, max: number): boolean {
  return min === max;
}

/**
 * Format a single price with currency symbol
 */
function formatPrice(price: number): string {
  return `${displayCurrencySymbol.value}${$currencyFormat(price)}`;
}

/**
 * Format a price range with currency symbols
 */
function formatPriceRange(min: number, max: number): string {
  return `${formatPrice(min)}-${formatPrice(max)}`;
}
</script>
